import { useState } from "react";
import { BiChevronLeft, BiChevronRight } from "react-icons/bi";
import { YouTubeEmbed } from '@next/third-parties/google';
import { Link, Image, ScrollShadow } from "@nextui-org/react";
import SharedService from "@/services/shared-service";
import { NEWS_TYPE } from '@/components/helpers/constant.js';

var _ = require('lodash');

/**
 * Video Player with List Component
 * Modern video player with sidebar list, similar to SaltForLife but with new UI
 * 
 * @param {Object} data - Video data
 * @param {Object} category - Category data: categoryCode, name, link
 * @param {number} maxVideos - Maximum number of videos to display (default: 10)
 */
export default function VideoPlayerList({
    data,
    maxVideos = 10,
    category
}) {
    // Filter videos that have YouTube embed ID
    let videoList = _.filter(data?.value || [], video => video?.mediaFileEmbedId);
    videoList = _.slice(videoList, 0, maxVideos);

    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);

    if (!videoList || videoList.length === 0) {
        return null;
    }

    const currentVideo = videoList[currentVideoIndex];

    const handleVideoSelect = (index) => {
        setCurrentVideoIndex(index);
        setIsPlaying(false);
    };

    const handleNext = () => {
        const nextIndex = currentVideoIndex === videoList.length - 1 ? 0 : currentVideoIndex + 1;
        setCurrentVideoIndex(nextIndex);
        setIsPlaying(false);
    };

    const handlePrev = () => {
        const prevIndex = currentVideoIndex === 0 ? videoList.length - 1 : currentVideoIndex - 1;
        setCurrentVideoIndex(prevIndex);
        setIsPlaying(false);
    };

    return (
        <div className="bg-white rounded-[5px] border border-t-0 shadow-md overflow-hidden">
            {/* Header */}
            <div className="bg-white px-6 py-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <Link href={category.link}>
                        <div>
                            <h2 className="font-bold text-xl text-primary">{category.name}</h2>
                        </div>
                    </Link>
                </div>
                <a
                    href={category.link}
                    className="text-primary hover:underline text-sm whitespace-nowrap flex items-center gap-1"
                >
                    <span>Xem tất cả</span>
                    <span>→</span>
                </a>
            </div>

            {/* Main Content: Video Player + List */}
            <div className="md:grid md:grid-cols-12 md:px-6 gap-4">
                {/* Left: Main Video Player */}
                <div className="md:col-span-9">
                    {/* Video Player Container with fixed aspect ratio */}
                    <div className="relative w-full overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                        {currentVideo?.mediaFileEmbedId ? (
                            <iframe
                                className="absolute inset-0 w-full h-full"
                                src={`https://www.youtube.com/embed/${currentVideo.mediaFileEmbedId}?rel=0&modestbranding=1`}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        ) : (
                            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-500">
                                <p>Video không khả dụng</p>
                            </div>
                        )}

                        {/* Navigation Arrows */}
                        {videoList.length > 1 && (
                            <>
                                <button
                                    onClick={handlePrev}
                                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white backdrop-blur-sm rounded-full p-3 transition-all shadow-lg"
                                    aria-label="Previous video"
                                >
                                    <BiChevronLeft className="w-6 h-6 text-gray-700" />
                                </button>
                                <button
                                    onClick={handleNext}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/90 hover:bg-white backdrop-blur-sm rounded-full p-3 transition-all shadow-lg"
                                    aria-label="Next video"
                                >
                                    <BiChevronRight className="w-6 h-6 text-gray-700" />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Video Info Below Player */}
                    <div className="p-4 bg-white border-t border-gray-200">
                        <h3 className="font-bold text-lg text-gray-900 line-clamp-2 mb-2">
                            {currentVideo?.title}
                        </h3>
                        {currentVideo?.metaDescription && (
                            <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                {currentVideo.metaDescription}
                            </p>
                        )}
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                            {currentVideo?.authorName && (
                                <span>👤 {currentVideo.authorName}</span>
                            )}
                            {currentVideo?.publishedDate && (
                                <span>📅 {SharedService.formatISODate(currentVideo.publishedDate, "DD/MM/YYYY")}</span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right: Video List */}
                <div className="md:col-span-3 bg-white border-l border-gray-200">
                    <ScrollShadow hideScrollBar className="h-[565px]" size={10}>
                        <div className="divide-y divide-gray-200">{videoList.map((video, index) => (
                            <button
                                key={video.id}
                                onClick={() => handleVideoSelect(index)}
                                className={`w-full text-left p-2.5 hover:bg-secondary transition-colors ${index === currentVideoIndex ? 'bg-blue-50 border-l-4 border-primary' : ''}`}
                            >
                                {/* Thumbnail */}
                                <div className="relative w-full aspect-video mb-2 rounded overflow-hidden bg-gray-200">
                                    {video?.photo ? (
                                        <Image
                                            width={1000}
                                            height={1000}
                                            src={SharedService.getOptimizeImage(video.photo, 'small-post-view')}
                                            alt={video.title}
                                            className="object-cover aspect-video"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                                            <span className="text-gray-500 text-2xl">▶️</span>
                                        </div>
                                    )}

                                    {/* Play Icon Overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-black/60 rounded-full p-2">
                                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </div>

                                    {/* Current Playing Indicator */}
                                    {index === currentVideoIndex && (
                                        <div className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded">
                                            Đang phát
                                        </div>
                                    )}
                                </div>

                                {/* Video Info */}
                                <h4 className={`text-sm font-semibold line-clamp-2 mb-1 ${index === currentVideoIndex ? 'text-primary' : 'text-gray-900'
                                    }`}>
                                    {video.title}
                                </h4>

                                {video?.authorName && (
                                    <p className="text-xs text-gray-500 mb-0.5">
                                        {video.authorName}
                                    </p>
                                )}

                                {video?.metaDescription && (
                                    <p className="text-xs text-gray-600 line-clamp-2 mb-0">
                                        {video.metaDescription}
                                    </p>
                                )}
                            </button>
                        ))}
                        </div>
                    </ScrollShadow>
                </div>
            </div>
        </div>
    );
}
